/**
 * Human-readable message for a Supabase Functions invoke error.
 *
 * `functions.invoke` throws a FunctionsHttpError on any non-2xx, but the real
 * payload lives in `error.context` (a Response) — `error.message` is only the
 * opaque "Edge Function returned a non-2xx status code". The function's OWN
 * errors are JSON `{ error }` (our helpers) or `{ msg }` (the edge runtime's
 * JWT gate), but infrastructure failures are not: a 404 from the gateway is
 * `text/plain` "Function not found" — i.e. the Edge Function is not deployed or
 * not being served (e.g. a freshly-added function before the local runtime is
 * restarted). We read the body ONCE as text, prefer its JSON message, and
 * otherwise surface the raw text + HTTP status so the operator gets an
 * actionable message instead of the opaque wrapper. Duck-typed on `context` so
 * we never import the error class.
 */
export async function fnErrorMessage(e: unknown): Promise<string> {
  const err = e as { message?: string; context?: Response } | null
  const ctx = err?.context
  if (ctx && typeof ctx.text === 'function') {
    const status = typeof ctx.status === 'number' ? ctx.status : 0
    let raw = ''
    try {
      raw = await ctx.text()
    } catch {
      /* body already consumed or unreadable */
    }
    const trimmed = raw.trim()
    if (trimmed) {
      try {
        const body = JSON.parse(trimmed) as { error?: string; message?: string; msg?: string }
        const msg = body?.error ?? body?.message ?? body?.msg
        if (msg) return msg
      } catch {
        /* non-JSON body (e.g. "Function not found") — fall through to raw text */
      }
    }
    if (status === 404) {
      return `Edge Function not found — it may not be deployed or served${
        trimmed ? ` (${trimmed})` : ''
      }.`
    }
    if (trimmed) return `${trimmed}${status ? ` (HTTP ${status})` : ''}`
    return err?.message ?? `Edge Function error${status ? ` (HTTP ${status})` : ''}`
  }
  return e instanceof Error ? e.message : String(e)
}
